import React, {ReactNode} from 'react'

import NavBar from './NavBar.js'
import NavBarLink from './NavBarLink.js'
import NavBarMenu from './NavBarMenu.js'
import type {Navigation, NavItem, NavBarBranding, NavBarHistory, NavBarLinkItem} from './types.js'

interface TopNavProps {
    navigation: Navigation
    /** Undefined while it loads */
    branding?: NavBarBranding | null
    history: NavBarHistory
    /** Rendered before the end items (the branch selector) */
    endSlot?: ReactNode
}

/**
 * The bar at the top of the screen, rendered from a Navigation (types.ts): the main items at the left (a group is a
 * dropdown), then endSlot (the branch selector), the end items as icons and the account menu at the right.
 */
export default function TopNav({navigation, branding, history, endSlot}: TopNavProps) {
    const {items, endItems, account} = navigation

    const link = (item: NavItem): NavBarLinkItem => ({icon: item.icon, title: item.title, target: item.target, onClick: item.onClick})

    return (
        <NavBar branding={branding as NavBarBranding}>
            {items.map((item) => (
                <NavBarLink
                    key={item.key}
                    history={history}
                    active={item.active}
                    link={link(item)}
                    links={item.children?.map((child) => ({...link(child), active: child.active}))}
                    branding={branding as NavBarBranding}
                />
            ))}

            <div style={{flex: 1}} />

            {endSlot}

            {/* Icons only, the side rail shows their titles */}
            {endItems.map((item) => (
                <NavBarLink
                    key={item.key}
                    history={history}
                    active={item.active}
                    link={{icon: item.icon, label: item.title, target: item.target, onClick: item.onClick}}
                    branding={branding as NavBarBranding}
                />
            ))}

            <NavBarMenu
                history={history}
                links={account.links}
                link={{icon: 'mdi mdi-account-circle', title: account.title, subTitle: account.subTitle}}
                branding={branding as NavBarBranding}
            />
        </NavBar>
    )
}
